/**
 * Enveloppe de l'application : écran courant, barre d'onglets, feuilles et
 * modales globales — EF-NAV-001, EF-NAV-006, EF-NAV-007.
 *
 * Trois écrans seulement, dont deux onglets permanents. Il n'y a ni pile de
 * retour ni table de parents : chaque onglet est atteignable en un geste
 * depuis n'importe où, et une feuille se referme sans destination.
 */

import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { observeVisibility } from '../platform/lifecycle';
import { ActionSheet } from './components/ActionSheet';
import { ConfirmationDialog } from './components/ConfirmationDialog';
import { FilterSheet } from './components/FilterSheet';
import { GotoSheet } from './components/GotoSheet';
import { ManualPoiSheet } from './components/ManualPoiSheet';
import { MessageList } from './components/MessageList';
import { ParameterImportDialog } from './components/ParameterImportDialog';
import { PurgeSheet } from './components/PurgeSheet';
import { RecalcSheet } from './components/RecalcSheet';
import { SearchProgressModal } from './components/SearchProgressModal';
import { TabBar } from './components/TabBar';
import { TrackSelectionDialog } from './components/TrackSelectionDialog';
import { RaceScreen } from './screens/RaceScreen';
import { RoadbookScreen } from './screens/RoadbookScreen';
import { SetupScreen } from './screens/SetupScreen';
import {
  confirmUpdateHealthy,
  initialize,
  resumeVisibility,
  screen,
  sheet,
  suspendVisibility,
  type ScreenName,
  type SheetName,
} from './state';

export function App(): JSX.Element {
  useEffect(() => {
    void initialize().then(() => {
      // Contrôle de bon fonctionnement après activation : les caches
      // antérieurs ne sont supprimés qu'ensuite.
      confirmUpdateHealthy();
    });
    return observeVisibility((visible) => {
      if (visible) void resumeVisibility();
      else suspendVisibility();
    });
  }, []);

  const name = screen.value;

  return (
    <>
      <main class="app-main">
        <div class="messages-slot">
          <MessageList />
        </div>
        <CurrentScreen name={name} />
      </main>

      {/* La barre d'onglets est masquée en mode course, et dans lui seul. */}
      {name === 'race' ? null : <TabBar />}

      <CurrentSheet name={sheet.value} />

      <ConfirmationDialog />
      <TrackSelectionDialog />
      <SearchProgressModal />
      <ParameterImportDialog />
    </>
  );
}

function CurrentScreen(props: { readonly name: ScreenName }): JSX.Element {
  switch (props.name) {
    case 'setup':
      return <SetupScreen />;
    case 'roadbook':
      return <RoadbookScreen />;
    case 'race':
      return <RaceScreen />;
  }
}

function CurrentSheet(props: { readonly name: SheetName | null }): JSX.Element | null {
  switch (props.name) {
    case null:
      return null;
    case 'actions':
      return <ActionSheet />;
    case 'goto':
      return <GotoSheet />;
    case 'recalc':
      return <RecalcSheet />;
    case 'filters':
      return <FilterSheet />;
    case 'manualPoi':
      return <ManualPoiSheet />;
    case 'purge':
      return <PurgeSheet />;
    case 'raceActions':
      // Rendu par RaceScreen lui-même : ses entrées dépendent d'état local
      // (projecteur de position, dernière position acceptée) non exposé
      // globalement.
      return null;
  }
}
